// The webui session rides a SESSION cookie (ported from tvirt-v6): unlike localStorage
// it dies with the browser. It holds the opaque `tims_` bearer token plus the display
// identity the portal shows — the cookie is the client's convenience store, the API
// remains the authority (bearer header).

export const SESSION_COOKIE = 'imago-session'

export type WebuiSession = {
  token: string
  username: string
  realm: string
  displayName: string
}

// A cookie of another shape (an older webui wrote `directory` before the realm rename,
// A20 2026-09-03) reads as NO session: the portal sends the browser back to the login,
// which rewrites the cookie — never a `user@undefined` in the header.
const isWebuiSession = (value: unknown): value is WebuiSession =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as WebuiSession).token === 'string' &&
  typeof (value as WebuiSession).username === 'string' &&
  typeof (value as WebuiSession).realm === 'string'

export function getSession(): WebuiSession | null {
  if (typeof document === 'undefined') {
    return null
  }

  const entry = document.cookie.split('; ').find(part => part.startsWith(`${SESSION_COOKIE}=`))

  if (!entry) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(entry.slice(SESSION_COOKIE.length + 1)))

    return isWebuiSession(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function setSession(session: WebuiSession) {
  // No expires/max-age: a session cookie, cleared when the browser closes. `Secure`
  // over HTTPS so the token-bearing cookie never rides plaintext (bughunt M15);
  // omitted on http (dev localhost) where a Secure cookie would not be stored.
  const secure = window.location.protocol === 'https:' ? '; secure' : ''

  document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(JSON.stringify(session))}; path=/; samesite=lax${secure}`
}

export function clearSession() {
  document.cookie = `${SESSION_COOKIE}=; path=/; samesite=lax; max-age=0`
}
